import type {
    Content,
    ContentSalesforceObjectsAssociation,
    GetContentSalesforceObjectAssociationsRequest,
} from "@amzn/ads-blink-client";
import { ContentType } from "@amzn/ads-blink-client";
import type { PitchedContent } from "@amzn/ads-discovery-theme-service-typescript-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { isEmpty } from "lodash";
import { useMemo } from "react";

import { getClientId } from "../../api/adsApi/adsApi.config";
import { getBlinkApiClientV2 } from "../../api/blinkApi/blinkApi";
import { getDiscoveryApiClient } from "../../api/discoveryApi/discoveryApi";
import { authStore } from "../../store/auth";
import type { AssociatedPitchedContent } from "../../store/editContent";
import { getAdvertiserId } from "../../utils/permission.utils";
import { useAutoFetch } from "../misc/useAutoFetch";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

export interface UseGetPitchedContentsProps {
    autoFetch?: boolean;
}

export function useGetPitchedContents({
    autoFetch = false,
}: UseGetPitchedContentsProps = {}): [
    AsyncState<AssociatedPitchedContent[]>,
    UseAsyncActions<AssociatedPitchedContent[]>,
] {
    const accountId = useMemo(() => getAdvertiserId(), []);

    const [state, action] = useAsync(async () => {
        const pitchedContents = await fetchPitchedContent();
        if (!pitchedContents.length) return [];

        const contents = pitchedContents.map(mapToContent);
        const associations = await fetchAssociations(contents);
        const associationsMap = buildAssociationsMap(associations, accountId);

        return enrichAndFilter(pitchedContents, associationsMap, accountId);
    }, []);

    useAutoFetch(action, autoFetch);
    useAsyncErrorStatusNotification(state);
    return [state, action];
}

async function fetchPitchedContent(): Promise<PitchedContent[]> {
    const client = await getDiscoveryApiClient();
    const response = await client.getPitchedContent();
    return response.contents ?? [];
}

async function fetchAssociations(
    contents: Content[],
): Promise<ContentSalesforceObjectsAssociation[]> {
    const request: GetContentSalesforceObjectAssociationsRequest = {
        userAlias: authStore.getState().username,
        clientId: getClientId(),
        contents,
    };
    const client = await getBlinkApiClientV2();
    const response =
        await client.getContentSalesforceObjectAssociations(request);
    return response.contentSalesforceObjectAssociations ?? [];
}

function buildAssociationsMap(
    associations: ContentSalesforceObjectsAssociation[],
    accountId?: string,
): Map<string, ContentSalesforceObjectsAssociation[]> {
    const map = new Map<string, ContentSalesforceObjectsAssociation[]>();

    associations.forEach((item) => {
        const contentId = item.content?.contentId;
        const matchesAccount =
            accountId !== undefined &&
            item.salesforceAssociation?.salesforceAccountId === accountId;

        if (contentId && matchesAccount) {
            const existing = map.get(contentId) ?? [];
            map.set(contentId, [...existing, item]);
        }
    });
    return map;
}

function mapToContent(pitchedContent: PitchedContent): Content {
    const { contentId, contentType } = pitchedContent;

    if (contentType === "PITCH") {
        return { contentId, contentType: ContentType.PITCHED_CONTENT };
    }

    if (Object.values(ContentType).includes(contentType as ContentType)) {
        return { contentId, contentType: contentType as ContentType };
    }

    return { contentId, contentType: undefined };
}

function enrichAndFilter(
    pitchedContents: PitchedContent[],
    associationsMap: Map<string, ContentSalesforceObjectsAssociation[]>,
    accountId?: string,
): AssociatedPitchedContent[] {
    return pitchedContents
        .map((content) => ({
            ...content,
            associations: associationsMap.get(content.contentId!) ?? [],
            accountOpportunities:
                accountId && content.accountOpportunities
                    ? content.accountOpportunities.filter(
                          (opp) => opp.accountId === accountId,
                      )
                    : [],
        }))
        .filter((content) => !isEmpty(content.associations));
}
